import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Snackbar, SnackbarContent } from '@mui/material';

const COUPON_CODE = 'DECOR10';

/** Dispatched when the header promo hits zero; LandingPage opens signup. */
export const DECORIFY_OPEN_SIGNUP_EVENT = 'decorify:open-signup';

// Each cycle length after expiry (and for brand-new visitors). Bump STORAGE_KEY when you change this
// so users are not stuck with an old deadline in localStorage.
const PROMO_EXTENSION_MS = 6 * 60 * 60 * 1000;
// v3: bump when PROMO_EXTENSION_MS changes so stale deadlines are not reused.
const PROMO_END_STORAGE_KEY = 'decorify_header_coupon_promo_end_v3';

function readStoredPromoEndMs() {
  try {
    const raw = localStorage.getItem(PROMO_END_STORAGE_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeStoredPromoEndMs(endMs) {
  try {
    localStorage.setItem(PROMO_END_STORAGE_KEY, String(endMs));
  } catch {
    // quota / private mode
  }
}

/** Same deadline across refreshes; seed full window from “now” when missing. */
function getOrInitPromoEndMs() {
  const stored = readStoredPromoEndMs();
  if (stored != null) return stored;
  const end = Date.now() + PROMO_EXTENSION_MS;
  writeStoredPromoEndMs(end);
  return end;
}

function getTimeLeftUntil(endMs) {
  const diff = endMs - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

function shouldOfferSignup(userData) {
  if (userData?.loading === true) return false;
  return !userData?.uid;
}

const pad = (n) => String(n).padStart(2, '0');

const HeaderCoupon = ({ userData }) => {
  const [isClosedCoupon, setIsClosedCoupon] = useState(false);
  const initialPromoEnd = useMemo(() => getOrInitPromoEndMs(), []);
  const promoEndRef = useRef(initialPromoEnd);
  const pendingSignupAfterAuthRef = useRef(false);
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeftUntil(initialPromoEnd));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!pendingSignupAfterAuthRef.current) return;
    if (shouldOfferSignup(userData)) {
      pendingSignupAfterAuthRef.current = false;
      window.dispatchEvent(new CustomEvent(DECORIFY_OPEN_SIGNUP_EVENT, { bubbles: true }));
    } else if (userData?.loading !== true && userData?.uid) {
      pendingSignupAfterAuthRef.current = false;
    }
  }, [userData]);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      let end = promoEndRef.current;
      if (end <= now) {
        if (shouldOfferSignup(userData)) {
          window.dispatchEvent(new CustomEvent(DECORIFY_OPEN_SIGNUP_EVENT, { bubbles: true }));
          pendingSignupAfterAuthRef.current = false;
        } else if (userData?.loading === true) {
          pendingSignupAfterAuthRef.current = true;
        }
        end = now + PROMO_EXTENSION_MS;
        promoEndRef.current = end;
        writeStoredPromoEndMs(end);
      }
      setTimeLeft(getTimeLeftUntil(end));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [userData]);

  const handleClaim = () => {
    setCopied(true);
    void navigator.clipboard?.writeText?.(COUPON_CODE)?.catch?.(() => {});
    if (shouldOfferSignup(userData)) {
      window.dispatchEvent(new CustomEvent(DECORIFY_OPEN_SIGNUP_EVENT, { bubbles: true }));
    }
    setTimeout(() => setIsClosedCoupon(true), 2800);
  };

  if (isClosedCoupon) return null;

  const timerNode = (
    <span className="header-coupon__timer">
      <span className="header-coupon__timer-segment">{pad(timeLeft.days)}</span>
      <span className="header-coupon__timer-sep">:</span>
      <span className="header-coupon__timer-segment">{pad(timeLeft.hours)}</span>
      <span className="header-coupon__timer-sep">:</span>
      <span className="header-coupon__timer-segment">{pad(timeLeft.minutes)}</span>
      <span className="header-coupon__timer-sep">:</span>
      <span className="header-coupon__timer-segment">{pad(timeLeft.seconds)}</span>
    </span>
  );

  const item = (
    <span className="header-coupon__item">
      <span className="header-coupon__label">10% OFF</span>
      <button className="header-coupon__cta" onClick={handleClaim}>
        {copied ? '✓ Copied!' : 'Claim now →'}
      </button>
      {timerNode}
      <span className="header-coupon__limited">
        Limited time offer:&nbsp;<strong>Use code {COUPON_CODE}</strong>
      </span>
      <button className="header-coupon__cta" onClick={handleClaim}>
        {copied ? '✓ Copied!' : 'Claim now →'}
      </button>
    </span>
  );

  return (
    <>
      <div className="header-coupon">
        <div className="header-coupon__track">
          {item}{item}{item}{item}
        </div>
        <button
          className="header-coupon__close"
          onClick={() => setIsClosedCoupon(true)}
          aria-label="Close banner"
        >
          ✕
        </button>
      </div>
      <Snackbar
        open={copied}
        onClose={() => setCopied(false)}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '50px !important', zIndex: 99999 }}
      >
        <SnackbarContent
          sx={{
            background: '#1a1a1a',
            color: '#fff',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}
          message={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>✓</span>
              <span>
                Code <strong style={{ color: '#f0a500' }}>{COUPON_CODE}</strong> copied — paste it at checkout!
              </span>
            </span>
          }
        />
      </Snackbar>
    </>
  );
};

export default HeaderCoupon;
